import type { BufferedDraft, DraftPayload } from "./draft-buffer";

/**
 * The autosave policy of [NF-022], React-free so it is unit-tested against a
 * fake clock: "fortlaufend als Entwurf sichern, ohne dass der Benutzer dies
 * ausloesen muss".
 *
 * - A change is saved once typing pauses for DRAFT_DEBOUNCE_MS, and at least
 *   every DRAFT_MAX_WAIT_MS while it does not - a long uninterrupted text is
 *   never more than that far from the server.
 * - Requests run strictly one after another, so a discard queued behind an
 *   in-flight save always lands last and nothing stale is resurrected.
 * - A failure the network or an expired session explains is retried with
 *   backoff and on `retryNow` (the browser's `online` event); the unconfirmed
 *   state stays in the volatile buffer meanwhile. A failure the request itself
 *   explains (400, 403, 404) is not retried.
 * - `hold` keeps saves back until the mask knows what is already stored, so
 *   the first keystroke cannot overwrite a state that is about to be offered.
 */

export type DraftSaveStatus = "idle" | "pending" | "saving" | "saved" | "retrying" | "failed";

export const DRAFT_DEBOUNCE_MS = 1_500;
export const DRAFT_MAX_WAIT_MS = 10_000;
export const DRAFT_RETRY_BASE_MS = 2_000;
export const DRAFT_RETRY_MAX_MS = 30_000;

/** Backoff before retry number `attempt` (1-based): 2 s, 4 s, 8 s, ... capped at 30 s. */
export function draftRetryDelayMs(attempt: number): number {
  return Math.min(DRAFT_RETRY_BASE_MS * 2 ** Math.max(0, attempt - 1), DRAFT_RETRY_MAX_MS);
}

/**
 * Worth retrying: no answer at all (offline), an expired session (the user may
 * sign in again in this page), a timeout, throttling or a server fault.
 */
export function isRetryableDraftFailure(status: number | undefined): boolean {
  return status === undefined || status === 401 || status === 408 || status === 429 || status >= 500;
}

export interface DraftSaverOptions {
  save: (payload: DraftPayload, opts: { keepalive: boolean }) => Promise<void>;
  remove: () => Promise<void>;
  /** HTTP status of a failed request, undefined when there was no answer. */
  statusOf: (error: unknown) => number | undefined;
  onStatus?: (status: DraftSaveStatus) => void;
  onSaved?: (payload: DraftPayload, at: number) => void;
  onRemoved?: () => void;
  now?: () => number;
  debounceMs?: number;
  maxWaitMs?: number;
}

type Work = { kind: "save"; payload: DraftPayload } | { kind: "remove" };

type Timer = ReturnType<typeof setTimeout>;

export class DraftSaver {
  private pending: Work | null = null;
  private chain: Promise<void> = Promise.resolve();
  private running = 0;
  private attempts = 0;
  private held: boolean;
  private disposed = false;
  private debounceTimer: Timer | null = null;
  private maxWaitTimer: Timer | null = null;
  private retryTimer: Timer | null = null;
  private current: DraftSaveStatus = "idle";

  constructor(
    private readonly options: DraftSaverOptions,
    init: { held?: boolean } = {},
  ) {
    this.held = init.held ?? false;
  }

  get status(): DraftSaveStatus {
    return this.current;
  }

  /** What is queued and not yet sent. */
  get pendingKind(): "save" | "remove" | null {
    return this.pending?.kind ?? null;
  }

  /** Something the server has not confirmed: queued, in flight, or waiting to retry. */
  get hasUnconfirmed(): boolean {
    return this.pending !== null || this.running > 0;
  }

  /** A change of the mask: its values to keep, or null when nothing is worth keeping. */
  schedule(payload: DraftPayload | null): void {
    if (this.disposed) return;
    this.pending = payload === null ? { kind: "remove" } : { kind: "save", payload };
    this.clear("retry");
    this.setStatus("pending");
    if (this.held) return;
    this.arm();
  }

  /** Stop holding saves back; anything changed meanwhile is scheduled now. */
  release(): void {
    if (!this.held) return;
    this.held = false;
    if (this.pending) this.arm();
  }

  /** Send what is queued now instead of after the pause. */
  flush(opts: { keepalive?: boolean } = {}): Promise<void> {
    this.clear("debounce");
    this.clear("maxWait");
    this.clear("retry");
    const work = this.pending;
    if (!work || (this.held && !opts.keepalive)) return this.chain;
    this.pending = null;
    return this.enqueue(work, opts.keepalive ?? false);
  }

  /** The browser is back online: try a waiting retry at once. */
  retryNow(): void {
    if (this.current === "retrying") void this.flush();
  }

  /** Drop what is queued without sending it. An in-flight request still completes. */
  cancel(): Promise<void> {
    this.clear("debounce");
    this.clear("maxWait");
    this.clear("retry");
    this.pending = null;
    if (this.running === 0) this.setStatus("idle");
    return this.chain;
  }

  /** Drop what is queued and remove the stored state, after anything in flight ([AC-030]). */
  discard(): Promise<void> {
    void this.cancel();
    return this.enqueue({ kind: "remove" }, false);
  }

  /** The mask is gone: no more timers or status reports. Queued requests already running finish. */
  dispose(): void {
    this.disposed = true;
    this.clear("debounce");
    this.clear("maxWait");
    this.clear("retry");
  }

  private arm(): void {
    const debounceMs = this.options.debounceMs ?? DRAFT_DEBOUNCE_MS;
    const maxWaitMs = this.options.maxWaitMs ?? DRAFT_MAX_WAIT_MS;
    this.clear("debounce");
    this.debounceTimer = setTimeout(() => void this.flush(), debounceMs);
    if (this.maxWaitTimer === null) {
      this.maxWaitTimer = setTimeout(() => void this.flush(), maxWaitMs);
    }
  }

  private enqueue(work: Work, keepalive: boolean): Promise<void> {
    this.running += 1;
    this.chain = this.chain.then(() => this.run(work, keepalive));
    return this.chain;
  }

  private async run(work: Work, keepalive: boolean): Promise<void> {
    this.setStatus("saving");
    try {
      if (work.kind === "save") {
        await this.options.save(work.payload, { keepalive });
        this.options.onSaved?.(work.payload, this.now());
      } else {
        await this.options.remove();
        this.options.onRemoved?.();
      }
      this.attempts = 0;
      this.running -= 1;
      if (this.pending === null && this.running === 0) {
        this.setStatus(work.kind === "save" ? "saved" : "idle");
      }
    } catch (error) {
      this.running -= 1;
      // A newer change supersedes the one that failed; otherwise it waits.
      const retryable = isRetryableDraftFailure(this.options.statusOf(error));
      if (!retryable) {
        this.attempts = 0;
        this.setStatus("failed");
        return;
      }
      if (this.pending === null) this.pending = work;
      this.attempts += 1;
      this.setStatus("retrying");
      if (!this.disposed) {
        this.clear("retry");
        this.retryTimer = setTimeout(() => void this.flush(), draftRetryDelayMs(this.attempts));
      }
    }
  }

  private clear(which: "debounce" | "maxWait" | "retry"): void {
    const key = `${which}Timer` as const;
    const timer = this[key];
    if (timer !== null) clearTimeout(timer);
    this[key] = null;
  }

  private setStatus(status: DraftSaveStatus): void {
    this.current = status;
    if (!this.disposed) this.options.onStatus?.(status);
  }

  private now(): number {
    return this.options.now ? this.options.now() : Date.now();
  }
}

/** What a mask offers to restore: its newest known unsaved state. */
export interface RestoreCandidate {
  payload: DraftPayload;
  /** ISO time of that state. */
  at: string;
  /** "buffer" when the server has not confirmed it yet. */
  source: "server" | "buffer";
}

/**
 * The server's copy, unless this page holds a newer change the server never
 * confirmed - the connection dropped, or the session ran out, before it could.
 */
export function pickRestoreCandidate(
  server: { payload: DraftPayload; updatedAt: string } | null,
  buffered: BufferedDraft | undefined,
): RestoreCandidate | null {
  if (buffered && !buffered.synced) {
    const serverAt = server ? Date.parse(server.updatedAt) : Number.NEGATIVE_INFINITY;
    if (buffered.changedAt >= serverAt) {
      return { payload: buffered.payload, at: new Date(buffered.changedAt).toISOString(), source: "buffer" };
    }
  }
  return server ? { payload: server.payload, at: server.updatedAt, source: "server" } : null;
}
